import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { updateGeogebra } from '../../actions/homework';
import { base64ToBlob, getDeviceType, saveData } from '../../lib/utils';

import { showModal } from '../../actions/modal';
import { RESET_GEOGEBRA_MODAL } from '../../containers/modal/modal-types';
import Button from '../button';
import Stack from '../stack';

const Container = styled.div`
    width: 100%;
    max-width: 100%;
    position: relative;
    z-index: 0;
`;

const BarContainer = styled.div`
    max-width: 100%;
    min-height: 50px;
    padding: 8px;
    box-sizing: border-box;
    background-color: var(--gray50);

    & > div {
        justify-content: flex-end;
    }
`;

const Geogebra = ({ exerciseId, geogebraExercises, src, updateGeogebra, showModal }) => {
    let ggbbase64 = geogebraExercises.find((item) => item.id === exerciseId);

    const [applet, setApplet] = useState();
    const geoRef = useRef();
    const deviceType = getDeviceType();
    const [geogebraStatus, setGeogebraStatus] = useState({ updated: !!ggbbase64, loaded: false });

    useEffect(() => {
        const update = () => {
            if (applet && applet.getAppletObject() && geogebraStatus.loaded) {
                document.activeElement.blur();
                updateGeogebra(
                    exerciseId,
                    applet.getAppletObject().getBase64(),
                    applet.getAppletObject().getPNGBase64(1, false, undefined),
                );
            }
        };

        window.addEventListener('beforeunload', update);
        return () => {
            window.removeEventListener('beforeunload', update);
            update();
        };
    }, [applet, exerciseId, updateGeogebra, geogebraStatus.loaded]);

    const ref = useCallback(
        (node) => {
            const createApplet = () => {
                let parameters = {
                    appName: 'suite',
                    width: node.offsetWidth,
                    height: 496,
                    showToolBar: true,
                    preventFocus: true,
                    showMenuBar: true,
                    showSuggestionButtons: false,
                    showAlgebraInput: true,
                    appletOnLoad: (api) => {
                        api.evalCommand('SetPerspective("AG")');
                        api.registerClientListener(() => {
                            updateGeogebra(exerciseId, api.getBase64());
                            setGeogebraStatus({ updated: true, loaded: true });
                        });
                    },
                };

                if (ggbbase64) {
                    parameters = { ...parameters, ggbbase64: ggbbase64.geogebra };
                } else {
                    parameters = { ...parameters, filename: src };
                }

                const geogebraApplet = new window.GGBApplet(parameters, true);

                geogebraApplet.inject(`ggb-container-${exerciseId}`);

                setApplet(geogebraApplet);
            };

            if (node && !applet) {
                createApplet();
            } else if (node && applet && !ggbbase64 && geogebraStatus.updated) {
                setGeogebraStatus({ ...geogebraStatus, updated: false });
                applet.removeExistingApplet(`ggb-container-${exerciseId}`);
                createApplet();
            }
        },

        [applet, exerciseId, ggbbase64, src, updateGeogebra, geogebraStatus],
    );

    useEffect(() => {
        const handleResize = () => {
            applet.getAppletObject().setWidth(geoRef.current.offsetWidth);
        };

        if (applet && applet.getAppletObject()) {
            window.addEventListener('resize', handleResize);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [applet, geoRef]);

    const exportGeogebra = async () => {
        const fileName = `${exerciseId}.ggb`;
        const base64 = applet.getAppletObject().getBase64();
        const blob = await base64ToBlob(base64);

        if (blob !== null) {
            saveData([blob], fileName);
        }
    };

    return (
        <Container ref={geoRef}>
            <Container id={`ggb-container-${exerciseId}`} ref={ref} />
            <BarContainer>
                <Stack gap="small" direction={['vertical', 'horizontal']}>
                    {deviceType === 'DESKTOP' && (
                        <Button type="tertiary" size="small" onClick={exportGeogebra}>
                            {'Esporta'}
                        </Button>
                    )}
                    <Button
                        disabled={!ggbbase64}
                        type="tertiary"
                        onClick={() => {
                            showModal(RESET_GEOGEBRA_MODAL);
                        }}
                        size="small"
                    >
                        {'Ripristina originale'}
                    </Button>
                </Stack>
            </BarContainer>
        </Container>
    );
};

Geogebra.propTypes = {
    exerciseId: PropTypes.number,
    geogebraExercises: PropTypes.array,
    showModal: PropTypes.func,
    src: PropTypes.string,
    updateGeogebra: PropTypes.func,
};

const mapStateToProps = (state) => {
    return {
        geogebraExercises: state.homework.geogebraExercises,
    };
};

const actionCreators = {
    showModal,
    updateGeogebra,
};

export default connect(mapStateToProps, actionCreators)(Geogebra);
